import { ServiceProviderApi } from '@business/api/tm_service_provider';
import {
  ProviderSearchParams,
  ServiceProviderId,
  TmServiceProvidersResponse,
  CompactProviderResponse,
  ProviderCategoryRegionParams,
  VeryCompactProviderResponse,
} from '@business/entities/tm/TmServiceProviderResponse';
import { handleMessage, requestMessage } from '@business/messages';
import { BaseBloc } from '@core/utils/bloc';
import { Repository } from '@core/utils/repository';
import { askChangeReason } from '@modules/common/ChangeReasonModal';
import { ServiceProviderFormData } from '@modules/settings/SettingsPage/Components/EstimateTab/AddEditServiceProviderModal/ProviderForm';
import { switchMap, tap } from 'rxjs/operators';

export class ServiceProvidersBloc extends BaseBloc {
  private readonly tmProviderRepo = new Repository<TmServiceProvidersResponse | CompactProviderResponse | VeryCompactProviderResponse>({
    getItemId: item => item.id,
  });

  readonly items$ = this.tmProviderRepo.items$;
  readonly selectTemplate = this.tmProviderRepo.selectItem;

  onReset() {
    this.tmProviderRepo.reset();
  }

  fetchServiceProviders = (query?: ProviderSearchParams) => {
    return ServiceProviderApi.fetchServiceProviders(query).pipe(
      this.tmProviderRepo.ops.reset(),
      handleMessage({ error: requestMessage('fetch_service_provider_error') }),
    );
  };

  fetchServiceProvider = (id: ServiceProviderId) => {
    return ServiceProviderApi.fetchServiceProvider(id).pipe(
      this.tmProviderRepo.ops.upsertOne(item => ({ item })),
      handleMessage({ error: requestMessage('fetch_service_provider_error') }),
    );
  };

  createServiceProvider = (data: ServiceProviderFormData) => {
    return ServiceProviderApi.createServiceProvider(data).pipe(
      this.tmProviderRepo.ops.addOne(item => item),
      handleMessage({
        type: requestMessage('create_service_provider'),
      }),
    );
  };

  // Edits and deletes ask for the reason first (askChangeReason, kept in the Activity Log); closing that window saves
  // nothing
  updateServiceProvider = (id: ServiceProviderId, data: ServiceProviderFormData) => {
    return askChangeReason().pipe(
      switchMap(changeNote => ServiceProviderApi.updateServiceProvider(id, data, changeNote)),
      this.tmProviderRepo.ops.upsertOne(item => ({ item })),
      tap(() => this.fetchServiceProviders().subscribe()),
      handleMessage({
        type: requestMessage('update_service_provider'),
      }),
    );
  };

  deleteServiceProvider = (id: ServiceProviderId) => {
    return askChangeReason('Delete Vendor', 'Do you really want to remove this vendor?', 'Delete').pipe(
      switchMap(changeNote => ServiceProviderApi.deleteServiceProvider(id, changeNote)),
      this.tmProviderRepo.ops.removeOne(() => id),
      handleMessage({
        type: requestMessage('delete_service_provider'),
      }),
    );
  };

  fetchServiceProvidersBaseOnCategoryRegion = (query?: ProviderCategoryRegionParams) => {
    return ServiceProviderApi.fetchServiceProvidersBaseOnCategoryRegion(query).pipe(
      this.tmProviderRepo.ops.reset(),
      handleMessage({ error: requestMessage('fetch_service_provider_error') }),
    );
  };
}
